import React, { createContext, useContext, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useSession } from '@/hooks/useSession'
import { SessionTimeoutModal } from '@/components/session/SessionTimeoutModal'
import { useAuthStore } from '@/store/auth'

interface SessionContextType {
  timeRemainingMinutes: number
  isSessionActive: boolean
  extendSession: () => void
  forceLogout: () => void
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

interface SessionProviderProps {
  children: ReactNode
}

export function SessionProvider({ children }: SessionProviderProps) {
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  
  // Check if we're on an authentication page
  const isAuthPage = location.pathname.startsWith('/login') || 
                     location.pathname.startsWith('/two-factor') || 
                     location.pathname.startsWith('/forgot-password') ||
                     location.pathname.startsWith('/reset-password') ||
                     location.pathname === '/'
  
  // Only use session if authenticated and not on auth page
  const shouldUseSession = isAuthenticated && !isAuthPage
  
  // Always call hook (React rules), but it will handle disabling internally
  const {
    timeRemainingMinutes,
    isSessionActive,
    extendSession,
    forceLogout,
    showWarning
  } = useSession()

  const contextValue: SessionContextType = {
    timeRemainingMinutes,
    isSessionActive,
    extendSession,
    forceLogout
  }

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
      {/* Only show session timeout modal if authenticated and not on auth page */}
      {shouldUseSession && showWarning && (
        <SessionTimeoutModal
          isOpen={showWarning}
          timeRemainingMinutes={timeRemainingMinutes}
          onExtend={extendSession}
          onLogout={forceLogout}
        />
      )}
    </SessionContext.Provider>
  )
}

export function useSessionContext() {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSessionContext must be used within a SessionProvider')
  }
  return context
}

